import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, ArrowLeft, ArrowUpRight, RotateCcw } from 'lucide-react';
import { Button } from '@/components/Button';
import { Card, CardBody } from '@/components/Card';
import { cn } from '@/lib/cn';
import { ControleDeResponsavel } from '@/features/tickets/AssigneeControl';
import { AttachmentsPanel } from '@/features/tickets/AttachmentsPanel';
import { BadgeDeTier, BarraDeProgresso, EtiquetaDePrioridade, EtiquetaDeStatus, EtiquetaDeTipo } from '@/features/tickets/badges';
import { ChildrenPanel } from '@/features/tickets/ChildrenPanel';
import { CommentsPanel } from '@/features/tickets/CommentsPanel';
import { formatarDataHora, formatarDistancia, mensagemDeErro } from '@/lib/format';
import { LinkDaLista, useAbaDoChamado, type AbaDoChamado } from '@/features/tickets/navigation';
import { PainelDeMudanca } from '@/features/changes/PainelDeMudanca';
import { SugestoesDeErroConhecido } from '@/features/kedb/SugestoesDeErroConhecido';
import { SugestoesDeArtigo } from '@/features/knowledge/SugestoesDeArtigo';
import { PainelDeIa } from '@/features/tickets/PainelDeIa';
import {
  nomeDaEquipe,
  nomeDaOrganizacao,
  rotuloDaImpacto,
  rotuloDaUrgencia,
  useOpcoesDeFormulario,
} from '@/features/tickets/opcoes';
import { NomeDePessoa, useDicionarioDePessoas } from '@/features/tickets/pessoas';
import { SlaPanel } from '@/features/tickets/SlaPanel';
import { ControleDeSituacao } from '@/features/tickets/StatusControl';
import { enderecoDoContrato, cobertura as infoDaCobertura } from '@/features/contratos/navegacao';
import { chaves, listarEscalonamentos, obterChamado } from '@/features/tickets/tickets.api';
import { type Chamado } from '@/features/tickets/types';
import { WorklogPanel } from '@/features/tickets/WorklogPanel';
import { EscalationPanel } from '@/features/tickets/EscalationPanel';
import { ClassificacaoControl } from '@/features/tickets/ClassificacaoControl';
import { ControleDeFluxo } from '@/features/workflow/ControleDeFluxo';
import { HistoricoDeFluxo } from '@/features/workflow/HistoricoDeFluxo';

type Aba = AbaDoChamado;

/**
 * Detalhe do chamado.
 *
 * Layout: coluna principal (smart panels + abas de conteúdo) + barra lateral
 * (situação, responsável, classificação, metadados). As ações ficam sempre
 * visíveis enquanto o operador navega o conteúdo.
 *
 * A aba corrente mora na URL: o calendário de mudanças pode apontar direto
 * para a ficha de Change Enablement, e um link que abre a aba errada obriga
 * quem clicou a procurar.
 */
export function TicketDetailPage({ ticketId }: { ticketId: string }) {
  const { aba: pedida, definir: setAba } = useAbaDoChamado(ticketId);

  const consulta = useQuery({
    queryKey: chaves.item(ticketId),
    queryFn: () => obterChamado(ticketId),
  });

  if (consulta.isPending) {
    return (
      <div className="mx-auto max-w-5xl space-y-4" aria-busy>
        <span className="sr-only">Carregando chamado</span>
        <div className="h-8 w-64 animate-pulse rounded-md bg-muted" />
        <div className="h-48 animate-pulse rounded-lg bg-muted" />
      </div>
    );
  }

  if (consulta.isError) {
    return (
      <div className="mx-auto max-w-5xl">
        <Card>
          <CardBody className="flex flex-col items-center gap-3 py-16 text-center">
            <p role="alert" className="text-sm text-destructive">
              {mensagemDeErro(consulta.error)}
            </p>
            <p className="max-w-md text-sm text-muted-foreground">
              Chamado fora do seu escopo e chamado inexistente respondem igual, de propósito — a
              existência do registro também é informação.
            </p>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void consulta.refetch()}
                icon={<RotateCcw className="h-4 w-4" aria-hidden />}
              >
                Tentar de novo
              </Button>
              <LinkDaLista className="inline-flex h-8 items-center rounded-md px-3 text-sm font-medium text-foreground transition-colors hover:bg-accent">
                Voltar para a lista
              </LinkDaLista>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  const chamado = consulta.data;

  const aba: Aba =
    (pedida === 'mudanca' && chamado.type !== 'CHANGE') ||
    (pedida === 'historico' && chamado.workflowVersionId === null)
      ? 'descricao'
      : pedida;

  return (
    <div className="mx-auto max-w-[1400px] space-y-5">
      <Cabecalho chamado={chamado} />

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-6">

        {/* ── Coluna principal: smart panels + conteúdo em abas ──── */}
        <div className="min-w-0 flex-1 space-y-4">

          {/* Sugestões automáticas — somem quando não há resultado */}
          {chamado.type === 'INCIDENT' || chamado.type === 'REQUEST' || chamado.type === 'PROBLEM' ? (
            <SugestoesDeErroConhecido ticketId={chamado.id} />
          ) : null}
          <SugestoesDeArtigo ticketId={chamado.id} />
          <PainelDeIa ticketId={chamado.id} />

          <Card>
            <div className="flex gap-1 overflow-x-auto border-b border-border px-2" role="tablist">
              <BotaoDeAba atual={aba} valor="descricao" aoEscolher={setAba}>
                Descrição
              </BotaoDeAba>
              {/* Comentários é a ação mais frequente — fica logo após a descrição */}
              <BotaoDeAba atual={aba} valor="comentarios" aoEscolher={setAba}>
                Comentários
              </BotaoDeAba>
              {chamado.type === 'CHANGE' ? (
                <BotaoDeAba atual={aba} valor="mudanca" aoEscolher={setAba}>
                  Mudança
                </BotaoDeAba>
              ) : null}
              <BotaoDeAba atual={aba} valor="subchamados" aoEscolher={setAba} contador={chamado.childCount}>
                Subchamados
              </BotaoDeAba>
              {chamado.workflowVersionId !== null ? (
                <BotaoDeAba atual={aba} valor="historico" aoEscolher={setAba}>
                  Histórico
                </BotaoDeAba>
              ) : null}
              <BotaoDeAba atual={aba} valor="sla" aoEscolher={setAba}>
                SLA
              </BotaoDeAba>
              <BotaoDeAba atual={aba} valor="tempo" aoEscolher={setAba}>
                Tempo
              </BotaoDeAba>
              <BotaoDeAba atual={aba} valor="anexos" aoEscolher={setAba}>
                Anexos
              </BotaoDeAba>
              <BotaoDeAba atual={aba} valor="escalonamentos" aoEscolher={setAba}>
                Escalonamentos
              </BotaoDeAba>
            </div>

            <CardBody
              id={`painel-${aba}`}
              role="tabpanel"
              aria-labelledby={`aba-${aba}`}
              className="animate-fade-in"
            >
              {aba === 'descricao' ? <Descricao chamado={chamado} /> : null}
              {aba === 'mudanca' && chamado.type === 'CHANGE' ? (
                <PainelDeMudanca ticketId={chamado.id} />
              ) : null}
              {aba === 'subchamados' ? <ChildrenPanel chamado={chamado} /> : null}
              {aba === 'comentarios' ? <CommentsPanel ticketId={chamado.id} /> : null}
              {aba === 'historico' ? <HistoricoDeFluxo chamado={chamado} /> : null}
              {aba === 'sla' ? <SlaPanel ticketId={chamado.id} /> : null}
              {aba === 'tempo' ? <WorklogPanel ticketId={chamado.id} /> : null}
              {aba === 'anexos' ? <AttachmentsPanel ticketId={chamado.id} /> : null}
              {aba === 'escalonamentos' ? (
                <EscalationPanel ticketId={chamado.id} statusCategory={chamado.statusCategory} />
              ) : null}
            </CardBody>
          </Card>
        </div>

        {/* ── Barra lateral: ações e metadados ─────────────────────── */}
        <aside className="w-full space-y-3 lg:w-[272px] lg:shrink-0">

          {/* Banner de triagem — aparece quando o chamado ainda não foi triado */}
          <TriagemPendente chamado={chamado} />

          {/* Situação */}
          <Card>
            <CardBody>
              <ControleDeFluxo chamado={chamado}>
                <ControleDeSituacao chamado={chamado} />
              </ControleDeFluxo>
            </CardBody>
          </Card>

          {/* Responsável */}
          <Card>
            <CardBody>
              <ControleDeResponsavel chamado={chamado} />
            </CardBody>
          </Card>

          {/* Classificação — abre em modo edição para chamados não triados */}
          <ClassificacaoControl
            chamado={chamado}
            defaultAberto={chamado.statusCategory === 'NEW' && !chamado.teamId}
          />

          {/* Metadados de contexto */}
          <MetadadosDoChamado chamado={chamado} />

          {/* Escalação — visível enquanto o chamado estiver aberto em N1 */}
          {chamado.statusCategory !== 'RESOLVED' &&
          chamado.statusCategory !== 'CLOSED' &&
          chamado.statusCategory !== 'CANCELLED' &&
          (chamado.supportTier === null || chamado.supportTier <= 1) ? (
            <button
              type="button"
              onClick={() => setAba('escalonamentos')}
              disabled={!chamado.teamId}
              title={!chamado.teamId ? 'Atribua uma equipe antes de escalar' : undefined}
              className="flex w-full items-center justify-between gap-2 rounded-xl border border-border bg-card px-4 py-3 text-left shadow-card transition-colors hover:bg-accent focus-visible:outline-none focus-visible:shadow-glow disabled:cursor-not-allowed disabled:opacity-50"
            >
              <div>
                <p className="text-sm font-medium text-foreground">Escalar chamado</p>
                <p className="text-xs text-muted-foreground">
                  {chamado.teamId ? 'Encaminhar para N2 ou superior' : 'Atribua uma equipe primeiro'}
                </p>
              </div>
              <ArrowUpRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
            </button>
          ) : null}

          {/* Progresso de subchamados */}
          {chamado.childCount > 0 ? (
            <div className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-xs text-muted-foreground shadow-card">
              <BarraDeProgresso percentual={chamado.progressPct} className="flex-1" />
              <span className="whitespace-nowrap tabular-nums">
                {chamado.progressPct}% · {chamado.openChildCount}/{chamado.childCount}
              </span>
            </div>
          ) : null}
        </aside>
      </div>
    </div>
  );
}

// ── triagem pendente (sidebar) ────────────────────────────────────────────

function TriagemPendente({ chamado }: { chamado: Chamado }) {
  if (chamado.statusCategory !== 'NEW') return null;

  const faltando = [
    !chamado.teamId && 'equipe',
    !chamado.assigneeId && 'responsável',
    !chamado.categoryId && 'categoria',
  ].filter(Boolean) as string[];

  if (faltando.length === 0) return null;

  return (
    <div className="flex items-start gap-3 rounded-xl border border-warning/40 bg-warning/8 px-4 py-3">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />
      <div className="min-w-0">
        <p className="text-sm font-semibold text-warning">Triagem pendente</p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Preencha: {faltando.join(', ')}
        </p>
      </div>
    </div>
  );
}

// ── cabeçalho ──────────────────────────────────────────────────────────────

function Cabecalho({ chamado }: { chamado: Chamado }) {
  const escalonamentos = useQuery({
    queryKey: chaves.escalonamentos(chamado.id),
    queryFn: () => listarEscalonamentos(chamado.id),
    staleTime: 30_000,
  });
  const contadorDeEscalonamentos = escalonamentos.data?.length ?? 0;

  return (
    <header className="space-y-2">
      <LinkDaLista className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground">
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Chamados
      </LinkDaLista>

      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-sm font-semibold text-gradient">{chamado.number}</span>
        <EtiquetaDeTipo tipo={chamado.type} />
        <EtiquetaDeStatus status={chamado.statusCategory} />
        <EtiquetaDePrioridade
          prioridade={chamado.priority}
          sobrescrita={chamado.priorityOverridden}
        />
        <BadgeDeTier tier={chamado.supportTier} escalacoes={contadorDeEscalonamentos} />
        {chamado.coverage !== 'NOT_APPLICABLE' ? (
          <CoberturaDoContrato coverage={chamado.coverage} />
        ) : null}
        {chamado.reopenCount > 0 ? (
          <span className="text-xs text-muted-foreground">
            reaberto {chamado.reopenCount}×
          </span>
        ) : null}
      </div>

      <h1 className="text-2xl font-semibold leading-tight tracking-tight">{chamado.subject}</h1>
    </header>
  );
}

// ── metadados de contexto (sidebar) ───────────────────────────────────────

function MetadadosDoChamado({ chamado }: { chamado: Chamado }) {
  const pessoas = useDicionarioDePessoas([chamado.requesterId]);
  const opcoes = useOpcoesDeFormulario();

  return (
    <Card>
      <CardBody>
        <dl className="space-y-2.5">
          <InfoLinha
            termo="Vencimento SLA"
            titulo={formatarDataHora(chamado.dueAt)}
            tom={
              chamado.dueAt && chamado.closedAt === null && new Date(chamado.dueAt) < new Date()
                ? 'text-destructive font-semibold'
                : undefined
            }
          >
            {formatarDistancia(chamado.dueAt)}
          </InfoLinha>
          <InfoLinha
            termo="1ª resposta"
            titulo={formatarDataHora(chamado.firstResponseAt)}
          >
            {chamado.firstResponseAt ? formatarDistancia(chamado.firstResponseAt) : '—'}
          </InfoLinha>
          <InfoLinha termo="Solicitante">
            <NomeDePessoa id={chamado.requesterId} pessoas={pessoas} />
          </InfoLinha>
          <InfoLinha termo="Equipe">
            <NomeDeCadastro
              nome={nomeDaEquipe(opcoes.data, chamado.teamId)}
              id={chamado.teamId}
              carregando={opcoes.isPending}
              semId="sem equipe"
            />
          </InfoLinha>
          <InfoLinha termo="Organização">
            <NomeDeCadastro
              nome={nomeDaOrganizacao(opcoes.data, chamado.organizationId)}
              id={chamado.organizationId}
              carregando={opcoes.isPending}
            />
          </InfoLinha>
          <InfoLinha termo="Impacto × urgência">
            {rotuloDaImpacto(opcoes.data, chamado.impact)} × {rotuloDaUrgencia(opcoes.data, chamado.urgency)}
          </InfoLinha>
          <InfoLinha termo="Aberto em">
            {formatarDataHora(chamado.openedAt)}
          </InfoLinha>
          {chamado.closedAt ? (
            <InfoLinha termo="Encerrado em">
              {formatarDataHora(chamado.closedAt)}
            </InfoLinha>
          ) : null}
          {chamado.contractId ? (
            <InfoLinha termo="Contrato">
              <Link
                {...enderecoDoContrato(chamado.contractId)}
                className="text-primary underline-offset-2 hover:underline"
              >
                Ver contrato
              </Link>
            </InfoLinha>
          ) : null}
        </dl>
      </CardBody>
    </Card>
  );
}

function InfoLinha({
  termo,
  titulo,
  tom,
  children,
}: {
  termo: string;
  titulo?: string;
  tom?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 min-w-0">
      <dt className="shrink-0 text-xs text-muted-foreground">{termo}</dt>
      <dd
        className={cn('truncate text-right text-xs font-medium', tom)}
        title={titulo}
      >
        {children}
      </dd>
    </div>
  );
}

function NomeDeCadastro({
  nome,
  id,
  carregando,
  semId = '—',
}: {
  nome: string | null;
  id: string | null;
  carregando: boolean;
  semId?: string;
}) {
  if (!id) return <span className="italic text-muted-foreground">{semId}</span>;
  if (nome) return <span title={id}>{nome}</span>;
  if (carregando) return <span className="text-muted-foreground">carregando…</span>;
  return (
    <span
      className="italic text-muted-foreground"
      title={`Identificador ${id} — sem cadastro correspondente`}
    >
      não identificado
    </span>
  );
}

// ── cobertura contratual ───────────────────────────────────────────────────

function CoberturaDoContrato({ coverage }: { coverage: string }) {
  const info = infoDaCobertura(coverage as Parameters<typeof infoDaCobertura>[0]);
  const TONS: Record<string, string> = {
    sucesso: 'bg-success/12 text-success ring-success/25',
    alerta: 'bg-warning/12 text-warning ring-warning/25',
    info: 'bg-primary/10 text-primary ring-primary/25',
    neutro: 'bg-muted text-muted-foreground ring-border',
    perigo: 'bg-destructive/12 text-destructive ring-destructive/25',
  };
  return (
    <span
      title={info.efeito}
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ring-inset',
        TONS[info.tom] ?? TONS['neutro'],
      )}
    >
      {info.rotulo}
    </span>
  );
}

// ── descrição ──────────────────────────────────────────────────────────────

/**
 * Mostra o texto, não o HTML.
 *
 * A descrição do chamado não passa pelo higienizador do servidor — renderizar
 * `descriptionHtml` aqui abriria XSS armazenado. O dia em que a API higienizar
 * a descrição, esta seção passa a renderizar marcação como a de comentários.
 */
function Descricao({ chamado }: { chamado: Chamado }) {
  const texto = chamado.descriptionText?.trim();

  if (!texto) {
    return <p className="text-sm text-muted-foreground">Este chamado foi aberto sem descrição.</p>;
  }

  return (
    <div className="space-y-4">
      <p className="whitespace-pre-wrap text-sm leading-relaxed">{texto}</p>

      {chamado.resolutionNote ? (
        <div className="rounded-md border border-success/25 bg-success/5 px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-success">
            Nota de resolução
          </p>
          <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed">
            {chamado.resolutionNote}
          </p>
        </div>
      ) : null}
    </div>
  );
}

// ── abas ───────────────────────────────────────────────────────────────────

function BotaoDeAba({
  atual,
  valor,
  contador,
  aoEscolher,
  children,
}: {
  atual: Aba;
  valor: Aba;
  contador?: number;
  aoEscolher: (aba: Aba) => void;
  children: ReactNode;
}) {
  const ativa = atual === valor;
  return (
    <button
      type="button"
      role="tab"
      id={`aba-${valor}`}
      aria-selected={ativa}
      aria-controls={`painel-${valor}`}
      onClick={() => aoEscolher(valor)}
      className={cn(
        'relative whitespace-nowrap px-3 py-3 text-sm font-medium transition-colors',
        ativa ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
      {contador !== undefined && contador > 0 ? (
        <span className="ml-1.5 rounded-full bg-muted px-1.5 py-0.5 text-[11px] tabular-nums text-muted-foreground">
          {contador}
        </span>
      ) : null}
      {ativa ? (
        <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-primary" />
      ) : null}
    </button>
  );
}
